/**
 * Presentation transforms for the concierge briefing markdown.
 *
 * The briefing is agent-authored in almanac-wiki. The concierge procedure
 * asks for short-label links (`api#907`) and `##` section headings, but the
 * renderer cannot depend on the author getting that right — these transforms
 * normalize whatever landed in the wiki into a parseable, link-pretty shape.
 */

/** Repo → short link-label prefix, matching the org-wide PR shorthand. */
const REPO_PREFIXES: Record<string, string> = {
  'langston-web': 'web',
  'langston-api': 'api',
  'db-migrations': 'db',
  'merge-service-fastapi': 'merge',
  'ai-search-indexer': 'indexer',
  'langston-infra': 'infra',
  almanac: 'almanac',
  'almanac-wiki': 'wiki',
  'ai-tools': 'ai-tools',
};

/** In-app route prefix the viewer intercepts to open a package doc panel. */
export const PACKAGE_ROUTE_PREFIX = '/briefing/packages/';

/**
 * GitHub-style anchor slug for a section heading, so the concierge's in-page
 * links (`[Quad Map Chart](#quad-map-chart)`) land on the rendered section.
 */
export function headingSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** GitHub location of a package doc, for the panel's "open on GitHub" link. */
export function packageGithubUrl(slug: string): string {
  return `https://github.com/the-langston-co/almanac-wiki/blob/main/packages/${slug}.md`;
}

// Section headings that are the briefing's own apparatus rather than a
// package card — current structure plus the legacy lens-first names, so old
// briefings classify correctly too.
const META_SECTION_SLUGS = new Set([
  'the-short-version',
  'quick-review-batches',
  'coverage-and-gaps',
  'decisions-needed',
  'blocked-on-you',
  'suggested-order-of-attack',
  'batchable',
  'needs-real-thought',
  'package-cards',
  'delta',
]);

/**
 * True when a briefing section is a per-package card rather than briefing
 * apparatus (the short version, review batches, coverage). A heading the
 * renderer has never seen classifies as a package — new packages must never
 * silently fall out of the card treatment.
 */
export function isPackageSection(title: string): boolean {
  return !META_SECTION_SLUGS.has(headingSlug(title));
}

// A bare URL is one not already serving as a markdown link target `](url)`
// or link text `[url]`; both cases put `(` or `[` immediately before it.
const BARE_GITHUB_PR = /(^|[^([])(https:\/\/github\.com\/the-langston-co\/([\w.-]+)\/pull\/(\d+))/g;
const BARE_LINEAR_ISSUE =
  /(^|[^([])(https:\/\/linear\.app\/[\w-]+\/issue\/([A-Za-z]+-\d+)[\w/-]*)/g;
const BARE_SLACK_THREAD = /(^|[^([])(https:\/\/[\w-]+\.slack\.com\/archives\/[\w/]+)/g;

// Package-doc links — wiki-relative (`](../packages/pkg-x.md)`) or already
// pointing at GitHub — become in-app routes so the viewer can open them in
// the package panel instead of leaving the app.
// The slug capture mirrors the API's server-side slug validator so the
// rewrite never produces an in-app route the endpoint would reject.
const PACKAGE_LINK =
  /\]\((?:(?:\.\.\/)?|https:\/\/github\.com\/the-langston-co\/almanac-wiki\/blob\/[\w.-]+\/)packages\/([a-z0-9][a-z0-9-]{0,127})\.md\)/g;

// A paragraph that is nothing but bold text is a de facto section heading.
const BOLD_LINE_HEADING = /^\*\*([^*]+)\*\*\s*$/;

// A fenced block, per CommonMark's rules the renderer actually applies:
// the opener is a line-leading run of 3+ of one delimiter (info string
// allowed), and it closes only on a line that is nothing but a same-char
// run at least as long — so shorter runs, mid-line runs, and runs with
// trailing text all stay inside the block. An unclosed fence runs to the
// end of the document. Named groups keep the backreferences readable when
// the alternatives are composed into larger patterns.
function fencedBlockSource(name: string, delimiter: string): string {
  const run = `(?<${name}>${delimiter}{3,})`;
  // `\r?` recognizes CRLF closing lines — raw wiki content is not
  // newline-normalized before it reaches these transforms.
  const closing = `[ \\t]{0,3}\\k<${name}>${delimiter}*[ \\t]*\\r?`;
  return `(?<=^|\\n)[ \\t]{0,3}${run}[^\\n]*(?:\\n(?!${closing}(?:\\n|$))[^\\n]*)*(?:\\n${closing}(?=\\n|$))?`;
}

// A code span is a fenced block (backtick or tilde) or an inline backtick
// run closed by a matching same-length run.
const CODE_SPAN_SOURCE = [
  fencedBlockSource('backtickFence', '`'),
  fencedBlockSource('tildeFence', '~'),
  '(?<inlineCode>`+)(?!`)[\\s\\S]*?\\k<inlineCode>(?!`)',
].join('|');

// Spans the prose transforms must never rewrite inside: fenced code, inline
// code, existing markdown links, and bare URLs.
const PROTECTED_SPAN = new RegExp(
  `${CODE_SPAN_SOURCE}|\\[[^\\]]*\\]\\([^)]*\\)|https?:\\/\\/[^\\s)]+`,
  'g',
);

// The URL/package-link rewrites *target* bare URLs and markdown links, so
// they only exclude the code spans — never any prose, even URL-shaped prose.
const CODE_SPAN = new RegExp(CODE_SPAN_SOURCE, 'g');

/** Applies `fn` to every stretch of text outside `protectedSpans` matches. */
function transformOutside(
  md: string,
  protectedSpans: RegExp,
  fn: (text: string) => string,
): string {
  let result = '';
  let last = 0;
  for (const match of md.matchAll(protectedSpans)) {
    result += fn(md.slice(last, match.index)) + match[0];
    last = match.index + match[0].length;
  }
  return result + fn(md.slice(last));
}

// Textual PR mentions with no URL — "DB #356", "web #935/#971",
// "web #1083, #1077, and #978" — carry enough to construct one. The first
// word picks the repo; every #number in the run gets its own link.
const PROSE_PR_MENTION =
  /\b(web|api|db|merge-service|merge|infra|indexer)(\s?#\d+(?:\s*(?:[,/]|,?\s+and)\s*#\d+)*)/gi;

/** Prose-mention prefix word → { link label, repo }. */
const MENTION_REPOS: Record<string, { label: string; repo: string }> = {
  web: { label: 'web', repo: 'langston-web' },
  api: { label: 'api', repo: 'langston-api' },
  db: { label: 'db', repo: 'db-migrations' },
  merge: { label: 'merge', repo: 'merge-service-fastapi' },
  'merge-service': { label: 'merge', repo: 'merge-service-fastapi' },
  infra: { label: 'infra', repo: 'langston-infra' },
  indexer: { label: 'indexer', repo: 'ai-search-indexer' },
};

const BARE_LINEAR_KEY = /\b(ENG-\d+)\b/g;

/** Links textual PR mentions and bare `ENG-####` keys in one prose chunk. */
function linkProseMentions(text: string): string {
  return text
    .replace(PROSE_PR_MENTION, (_match, word: string, numbers: string) => {
      const target = MENTION_REPOS[word.toLowerCase()];
      if (!target) return _match;
      return numbers
        .replace(
          /#(\d+)/g,
          (_hash, num: string) =>
            `[${target.label}#${num}](https://github.com/the-langston-co/${target.repo}/pull/${num})`,
        )
        .trimStart();
    })
    .replace(BARE_LINEAR_KEY, '[$1](https://linear.app/the-langston-co/issue/$1)');
}

/**
 * Rewrites bare URLs into short-label markdown links (`[api#907](…)`,
 * `[ENG-3906](…)`, `[Slack thread](…)`), links textual PR mentions and bare
 * `ENG-####` keys ("DB #356" → `[db#356](…)`), turns package-doc links into
 * in-app routes, and promotes bold-only lines to `##` headings.
 */
export function prettifyBriefingMarkdown(md: string): string {
  const linked = transformOutside(md, CODE_SPAN, (chunk) =>
    chunk
      .replace(BARE_GITHUB_PR, (match, prefix: string, url: string, repo: string, num: string) => {
        const label = REPO_PREFIXES[repo];
        if (!label) return match;
        return `${prefix}[${label}#${num}](${url})`;
      })
      .replace(
        BARE_LINEAR_ISSUE,
        (_match, prefix: string, url: string, key: string) => `${prefix}[${key}](${url})`,
      )
      .replace(
        BARE_SLACK_THREAD,
        (_match, prefix: string, url: string) => `${prefix}[Slack thread](${url})`,
      )
      .replace(PACKAGE_LINK, (_match, name: string) => `](${PACKAGE_ROUTE_PREFIX}${name})`),
  );

  // Heading promotion tracks fence state per line — a `**bold**` line inside
  // a fenced block is code, not a de facto heading. Per CommonMark, a fence
  // only closes on a whitespace-only line whose same-char run is at least as
  // long as the opener, so a ~~~~ block stays open across an internal ~~~
  // line and `**bold**` inside it is never promoted.
  let fence: { char: string; length: number } | null = null;
  return transformOutside(linked, PROTECTED_SPAN, linkProseMentions)
    .split('\n')
    .map((line) => {
      const fenceLine = /^\s{0,3}(`{3,}|~{3,})(.*)$/.exec(line);
      const run = fenceLine?.[1];
      const rest = fenceLine?.[2] ?? '';
      if (run && run[0]) {
        if (!fence) {
          fence = { char: run[0], length: run.length };
        } else if (run[0] === fence.char && run.length >= fence.length && rest.trim() === '') {
          fence = null;
        }
        return line;
      }
      if (fence) return line;
      const bold = BOLD_LINE_HEADING.exec(line)?.[1];
      if (bold) return `## ${bold.trim()}`;
      return line;
    })
    .join('\n');
}

/**
 * A package doc split into its frontmatter scalars and markdown body. Only
 * top-level `key: value` scalar lines are captured — nested structures
 * (like the `prs` list) stay in `raw` form inside the frontmatter and are
 * not needed for the panel header.
 */
export interface ParsedPackageDoc {
  fields: Record<string, string>;
  body: string;
}

const FRONTMATTER_SCALAR = /^([A-Za-z_][\w-]*):\s*(\S.*)$/;

/** Splits `---`-fenced frontmatter off a package doc. */
export function parsePackageDoc(md: string): ParsedPackageDoc {
  const fields: Record<string, string> = {};
  if (!md.startsWith('---\n')) return { fields, body: md };
  const end = md.indexOf('\n---', 4);
  if (end === -1) return { fields, body: md };
  for (const line of md.slice(4, end).split('\n')) {
    const scalar = FRONTMATTER_SCALAR.exec(line);
    if (scalar && scalar[1] && scalar[2]) {
      fields[scalar[1]] = scalar[2].replace(/^["']|["']$/g, '');
    }
  }
  const body = md.slice(end + 4).replace(/^\n+/, '');
  return { fields, body };
}

const LINKS_LINE = /^(\s*)[-*]\s*[*_]{0,2}Links[*_]{0,2}\s*[-—–:]*\s*(\S.*)$/;
const MD_LINK = /\[[^\]]+\]\(([^)]+)\)/g;

const LINK_GROUP_ORDER = ['Package', 'Pull requests', 'Linear', 'Threads', 'Other'] as const;

function linkGroup(href: string): (typeof LINK_GROUP_ORDER)[number] {
  if (href.startsWith(PACKAGE_ROUTE_PREFIX)) return 'Package';
  if (/github\.com\/[^/]+\/[^/]+\/pull\//.test(href)) return 'Pull requests';
  if (href.includes('linear.app/')) return 'Linear';
  if (href.includes('.slack.com/')) return 'Threads';
  return 'Other';
}

/**
 * Rewrites a package card's single `Links` bullet — one long comma run —
 * into a sub-bullet per link type (package doc, pull requests, Linear,
 * threads), so the row scans as labeled lines. Lines inside fenced code
 * (the session seed) are untouched. Run on card bodies after
 * {@link prettifyBriefingMarkdown} so hrefs are already normalized.
 */
export function groupPackageLinks(body: string): string {
  let fence: { char: string; length: number } | null = null;
  const out: string[] = [];
  for (const line of body.split('\n')) {
    const fenceLine = /^\s{0,3}(`{3,}|~{3,})(.*)$/.exec(line);
    const run = fenceLine?.[1];
    if (run && run[0]) {
      if (!fence) {
        fence = { char: run[0], length: run.length };
      } else if (
        run[0] === fence.char &&
        run.length >= fence.length &&
        (fenceLine?.[2] ?? '').trim() === ''
      ) {
        fence = null;
      }
      out.push(line);
      continue;
    }
    if (fence) {
      out.push(line);
      continue;
    }
    const linksLine = LINKS_LINE.exec(line);
    if (!linksLine) {
      out.push(line);
      continue;
    }
    const [, indent = '', rest = ''] = linksLine;
    const links = [...rest.matchAll(MD_LINK)];
    if (links.length < 2) {
      out.push(line);
      continue;
    }
    const groups = new Map<string, string[]>();
    for (const link of links) {
      const group = linkGroup(link[1] ?? '');
      const members = groups.get(group) ?? [];
      members.push(link[0]);
      groups.set(group, members);
    }
    out.push(`${indent}- *Links*`);
    for (const group of LINK_GROUP_ORDER) {
      const members = groups.get(group);
      if (!members) continue;
      out.push(`${indent}  - ${group}: ${members.join(', ')}`);
    }
  }
  return out.join('\n');
}

/** One `##` section of the briefing: its heading text and markdown body. */
export interface BriefingSection {
  title: string;
  body: string;
}

/** The briefing split for rendering: document title, meta line, sections. */
export interface ParsedBriefing {
  /** The leading `#` heading, if present. */
  title: string | null;
  /** The `Freshness: …` paragraph, if present, without the label. */
  freshness: string | null;
  /** Markdown between the title and the first section (rare). */
  preamble: string;
  sections: BriefingSection[];
}

/**
 * Normalizes (via {@link prettifyBriefingMarkdown}) and splits the briefing
 * into title, freshness meta, and `##` sections. Content under headings the
 * renderer doesn't recognize still comes through as ordinary sections —
 * this is presentation, not a data contract.
 */
export function parseBriefing(md: string): ParsedBriefing {
  const lines = prettifyBriefingMarkdown(md).split('\n');

  let title: string | null = null;
  let freshness: string | null = null;
  const preambleLines: string[] = [];
  const sections: BriefingSection[] = [];
  let current: { title: string; lines: string[] } | null = null;

  for (const line of lines) {
    const heading = /^##\s+(.+?)\s*$/.exec(line)?.[1];
    if (heading) {
      if (current) sections.push({ title: current.title, body: current.lines.join('\n').trim() });
      current = { title: heading, lines: [] };
      continue;
    }
    if (current) {
      current.lines.push(line);
      continue;
    }
    if (title === null && /^#\s+/.test(line)) {
      title = line.replace(/^#\s+/, '').trim();
      continue;
    }
    if (freshness === null && /^freshness:/i.test(line.trim())) {
      freshness = line.trim().replace(/^freshness:\s*/i, '');
      continue;
    }
    preambleLines.push(line);
  }
  if (current) sections.push({ title: current.title, body: current.lines.join('\n').trim() });

  return { title, freshness, preamble: preambleLines.join('\n').trim(), sections };
}
